import { queryOptions, useMutation } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useState } from 'react';

import { userQuery } from './user';

import { octokit, queryClient, api } from '@/lib/clients';

// Fun note: this endpoint returns installations which have an 'account' property which is a union of what GitHub calls 'simple-user' and 'enterprise-user'
// The 'simple-user' is a User or an Organization that uses GitHub cloud. Enterprise users are organizations that use GitHub Enterprise. Confusing!
// I'm narrowing this type to just simple-user (they have a 'login' property) because we only care about that now and it simplifies things downstream
export type Installation = Awaited<
  ReturnType<typeof octokit.rest.apps.listInstallationsForAuthenticatedUser>
>['data']['installations'][number] & {
  account: Extract<
    Awaited<
      ReturnType<typeof octokit.rest.apps.listInstallationsForAuthenticatedUser>
    >['data']['installations'][number]['account'],
    { login: string }
  >;
};

export const installationsQuery = () =>
  queryOptions({
    queryKey: ['installations'],
    queryFn: async ({ client }) => {
      const user = await client.ensureQueryData(userQuery);
      const installations = await octokit.paginate(octokit.rest.apps.listInstallationsForAuthenticatedUser, {
        per_page: 100,
      });
      const filtered = installations.filter((installation) =>
        installation.target_type === 'User' ? installation.target_id === user.id : true
      );
      return filtered as Installation[];
    },
    staleTime: Infinity,
  });

// Query for organization members
// export const organizationMembershipsQuery = (owner: string, installationId: number) =>
//   queryOptions({
//     queryKey: ['org', owner, 'memberships'],
//     queryFn: async () => {
//       console.log('getting memberships for', owner, installationId);
//       const res = await api.org[':owner'].memberships.$get({
//         param: { owner },
//         query: { installationId: installationId.toString() },
//       });
//       if (!res.ok) {
//         throw new Error('Failed to fetch organization memberships');
//       }
//       const data = await res.json();
//       return data.memberships;
//     },
//     staleTime: 30 * 1000, // 30 seconds
//   });

// export const organizationMembersQuery = (owner: string, installationId: number) =>
//   queryOptions({
//     queryKey: ['org', owner, 'members'],
//     queryFn: async () => {
//       console.log('getting members for', owner, installationId);
//       const res = await api.org[':owner'].members.$get({
//         param: { owner },
//         query: { installationId: installationId.toString() },
//       });
//       if (!res.ok) {
//         throw new Error('Failed to fetch organization members');
//       }
//       const data = await res.json();
//       return data.members;
//     },
//     staleTime: 30 * 1000, // 30 seconds
//   });

// Query for organization invites
// export const organizationInvitesQuery = (owner: string, installationId: number) =>
//   queryOptions({
//     queryKey: ['org', owner, 'invites'],
//     queryFn: async () => {
//       const res = await api.org[':owner'].invites.$get({
//         param: { owner },
//         query: { installationId: installationId.toString() },
//       });
//       if (!res.ok) {
//         throw new Error('Failed to fetch organization invites');
//       }
//       const data = await res.json();
//       return data.invitations;
//     },
//     staleTime: 30 * 1000, // 30 seconds
//   });

// // Combined mutations hook
// export const useOrgMutations = (owner: string, installationId: number) => {
//   const { mutate: inviteUsers, mutateAsync: inviteUsersAsync } = useMutation({
//     mutationFn: async (invites: { emailAddress: string; role: 'org:admin' | 'org:member' }[]) => {
//       const res = await api.org[':owner'].invites.$post({
//         param: { owner },
//         json: { invites, installationId },
//       });
//       if (!res.ok) {
//         throw new Error('Failed to invite users');
//       }
//       return res.json();
//     },
//     onSuccess: () => {
//       queryClient.invalidateQueries({ queryKey: ['org', owner, 'invites'] });
//     },
//   });

//   const { mutate: cancelInvite, mutateAsync: cancelInviteAsync } = useMutation({
//     mutationFn: async (inviteId: string) => {
//       const res = await api.org[':owner'].invites[':inviteId'].$delete({
//         param: { owner, inviteId },
//         json: { installationId },
//       });
//       if (!res.ok) {
//         throw new Error('Failed to cancel invitation');
//       }
//       return res.json();
//     },
//     onSuccess: () => {
//       queryClient.invalidateQueries({ queryKey: ['org', owner, 'invites'] });
//     },
//   });

//   const { mutate: updateMemberRole, mutateAsync: updateMemberRoleAsync } = useMutation({
//     mutationFn: async ({ userId, role }: { userId: string; role: 'org:admin' | 'org:member' }) => {
//       const res = await api.org[':owner'].members[':userId'].role.$patch({
//         param: { owner, userId },
//         json: { role, installationId },
//       });
//       if (!res.ok) {
//         throw new Error('Failed to update member role');
//       }
//       return res.json();
//     },
//     onSuccess: () => {
//       queryClient.invalidateQueries({ queryKey: ['org', owner, 'members'] });
//     },
//   });

//   const { mutate: removeMember, mutateAsync: removeMemberAsync } = useMutation({
//     mutationFn: async (userId: string) => {
//       const res = await api.org[':owner'].members[':userId'].$delete({
//         param: { owner, userId },
//         json: { installationId },
//       });
//       if (!res.ok) {
//         throw new Error('Failed to remove member');
//       }
//       return res.json();
//     },
//     onSuccess: () => {
//       queryClient.invalidateQueries({ queryKey: ['org', owner, 'members'] });
//     },
//   });

// return {
//   inviteUsers,
//   inviteUsersAsync,
//   cancelInvite,
//   cancelInviteAsync,
//   updateMemberRole,
//   updateMemberRoleAsync,
//   removeMember,
//   removeMemberAsync,
// };
// };

const CurrentOrgContext = createContext<{
  currentOrg: Installation | undefined;
  setCurrentOrg: (org: Installation) => void;
  clearCurrentOrg: () => void;
}>({
  currentOrg: undefined,
  setCurrentOrg: (_: Installation) => {},
  clearCurrentOrg: () => {},
});

export const useCurrentOrg = () => {
  const { currentOrg, setCurrentOrg, clearCurrentOrg } = useContext(CurrentOrgContext);
  if (!currentOrg) {
    throw new Error('No current org found');
  }
  return { currentOrg, setCurrentOrg, clearCurrentOrg };
};

export const useCurrentOrgMutators = () => {
  const { setCurrentOrg, clearCurrentOrg } = useContext(CurrentOrgContext);
  return {
    setCurrentOrg,
    clearCurrentOrg,
  };
};

export const CurrentOrgProvider = ({ children }: { children: React.ReactNode }) => {
  const [currentOrg, setCurrentOrg] = useState<Installation | undefined>(getCurrentOrg());

  useEffect(() => {
    if (currentOrg) setCurrentOrgStorage(currentOrg);
    console.log('changing localstorage org', currentOrg);
  }, [currentOrg]);

  return (
    <CurrentOrgContext.Provider
      value={{
        currentOrg,
        setCurrentOrg,
        clearCurrentOrg: () => {
          window.localStorage.removeItem('mesa-current-org');
        },
      }}
    >
      {children}
    </CurrentOrgContext.Provider>
  );
};

export const setCurrentOrgStorage = (org: Installation) => {
  window.localStorage.setItem('mesa-current-org', JSON.stringify(org));
  return Promise.resolve(org);
};

export const getCurrentOrg = () => {
  const orgSerialized = window.localStorage.getItem('mesa-current-org');
  if (!orgSerialized) {
    return undefined;
  }
  const org = JSON.parse(orgSerialized) as Installation;
  return org;
};
